import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialFilters = {
  city: '',
  realEstateTypeId: '',
  priceRange: 0,
  businessType: '',
};

const initialState = {
  data: [],
  filteredData: [],
  filters: initialFilters,
  status: 'idle',
  error: null,
  addStatus: 'idle',
  editStatus: 'idle',
  deleteStatus: 'idle',
  statusMessage: null,
};

const applyFilters = (data, filters) => {
  return data.filter((estate) => {
    const { city, realEstateTypeId, priceRange, businessType } = filters;
    const matchesCity = city
      ? estate.city.toLowerCase().includes(city.toLowerCase())
      : true;
    const matchesRealEstateType = realEstateTypeId
      ? estate.realEstateTypeId === realEstateTypeId
      : true;
    const matchesPriceRange = priceRange
      ? estate.price >= priceRange && estate.price <= 1000000000
      : true;
    const matchesBusinessType = businessType
      ? estate.businessType === businessType
      : true;

    return (
      matchesCity &&
      matchesRealEstateType &&
      matchesPriceRange &&
      matchesBusinessType
    );
  });
};

// GET request thunk
export const getRealEstates = createAsyncThunk(
  'realEstates/getRealEstates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios({
        method: 'GET',
        url: `${process.env.REACT_APP_API_URL}/RealEstates`,
        headers: {
          ContentType: 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data); // Updated to return error response data
    }
  }
);

// POST request thunk
export const addRealEstate = createAsyncThunk(
  'realEstates/addRealEstate',
  async ({ newRealEstate, token }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Agregar todos los campos
      Object.entries(newRealEstate).forEach(([key, value]) => {
        if (key === 'images') {
          // Si es un array de archivos
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((file) => formData.append('images', file));
          }
        } else if (key === 'coverImage') {
          // Si es la imagen principal (archivo único)
          if (value instanceof File) {
            formData.append('coverImage', value);
          }
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const response = await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_API_URL}/RealEstates`,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data', // Importante para enviar archivos
        },
        data: formData,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// PUT request thunk
export const editRealEstate = createAsyncThunk(
  'realEstates/editRealEstate',
  async ({ id, updatedRealEstate, token }, { rejectWithValue }) => {
    try {
      const response = await axios({
        method: 'PUT',
        url: `${process.env.REACT_APP_API_URL}/RealEstates/${id}`,
        headers: {
          ContentType: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        data: updatedRealEstate,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data); // Updated to return error response data
    }
  }
);

// DELETE request thunk
export const deleteRealEstate = createAsyncThunk(
  'realEstates/deleteRealEstate',
  async ({ realEstateId, token }, { rejectWithValue }) => {
    try {
      const response = await axios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_API_URL}/RealEstates/${realEstateId}`,
        headers: {
          ContentType: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data); // Actualizado para devolver los datos de la respuesta de error
    }
  }
);

export const realEstateSlice = createSlice({
  name: 'realEstate',
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
      state.filteredData = applyFilters(state.data, state.filters);
    },
    filterRealEstates(state) {
      state.filteredData = applyFilters(state.data, state.filters);
    },
    clearRealEstatesStatus(state) {
      state.addStatus = 'idle';
      state.editStatus = 'idle';
      state.deleteStatus = 'idle';
      state.statusMessage = null;
    },
    clearRealEstatesFilter(state) {
      state.filters = initialFilters;
      state.filteredData = state.data;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET request cases
      .addCase(getRealEstates.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getRealEstates.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        state.filteredData = applyFilters(action.payload, state.filters);
      })
      .addCase(getRealEstates.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // POST request cases
      .addCase(addRealEstate.pending, (state) => {
        state.addStatus = 'loading';
        state.editStatus = 'idle';
        state.deleteStatus = 'idle';
      })
      .addCase(addRealEstate.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        state.statusMessage = 'El inmueble ha sido creado exitosamente';
        state.data.unshift(action.payload); // Update the state with the new real estate
        state.filteredData.unshift(action.payload);
      })
      .addCase(addRealEstate.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.statusMessage = action.payload
          ? action.payload
          : 'Ha ocurrido un error inesperado y no se pudo procesar su solicitud';
      })
      // PUT request cases
      .addCase(editRealEstate.pending, (state) => {
        state.editStatus = 'loading';
        state.addStatus = 'idle';
        state.deleteStatus = 'idle';
      })
      .addCase(editRealEstate.fulfilled, (state, action) => {
        state.editStatus = 'succeeded';
        state.statusMessage = 'El inmueble ha sido editado exitosamente';
        const index = state.data.findIndex(
          (realEstate) => realEstate.id === action.payload.id
        );
        if (index !== -1) {
          state.data[index] = action.payload;
          state.filteredData[index] = action.payload; // Update the filtered data
        }
      })
      .addCase(editRealEstate.rejected, (state, action) => {
        state.editStatus = 'failed';
        state.statusMessage = action.payload
          ? action.payload
          : 'Ha ocurrido un error inesperado y no se pudo procesar su solicitud';
      })
      // DELETE request cases
      .addCase(deleteRealEstate.pending, (state) => {
        state.deleteStatus = 'loading';
        state.addStatus = 'idle';
        state.editStatus = 'idle';
      })
      .addCase(deleteRealEstate.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.statusMessage = 'El inmueble ha sido eliminado exitosamente';
        state.data = state.data.filter(
          (realEstate) => realEstate.id !== action.meta.arg.realEstateId
        ); // Remove the deleted real estate from the state
        state.filteredData = state.filteredData.filter(
          (realEstate) => realEstate.id !== action.meta.arg.realEstateId
        );
      })
      .addCase(deleteRealEstate.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.statusMessage = action.payload
          ? action.payload
          : 'Ha ocurrido un error inesperado y no se pudo procesar su solicitud';
      });
  },
});

export const {
  setFilters,
  filterRealEstates,
  clearRealEstatesStatus,
  clearRealEstatesFilter,
} = realEstateSlice.actions;

export default realEstateSlice.reducer;
